import React, { useState, useEffect } from 'react';
import { AlertCircle, StickyNote, Save, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

interface OnStopNoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerName: string;
  onSave: (note: string) => Promise<void>;
}

export function OnStopNoteDialog({
  open,
  onOpenChange,
  customerName,
  onSave,
}: Readonly<OnStopNoteDialogProps>) {
  const [note, setNote] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setNote('');
      setIsSubmitting(false);
    }
  }, [open]);

  const handleSave = async () => {
    // Validation
    if (!note.trim()) {
      toast.error('Please enter a reason for placing the customer on stop');
      return;
    }

    setIsSubmitting(true);

    try {
      await onSave(note.trim());
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving on stop note:', error);
      toast.error('Failed to save note');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Allow Ctrl+Enter or Cmd+Enter to submit
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-amber-600" />
            Add On Stop Note
          </DialogTitle>
          <DialogDescription>
            Please provide a reason for placing {customerName} on stop
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="note" className="flex items-center gap-2">
              <StickyNote className="h-4 w-4 text-muted-foreground" />
              Reason for On Stop <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="note"
              placeholder="Enter the reason why this customer is being placed on stop... (Ctrl+Enter to save)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onKeyDown={handleKeyDown}
              className="min-h-[150px] resize-none"
              disabled={isSubmitting}
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              This note will be saved to the customer's Notes tab
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            <X className="mr-2 h-4 w-4" />
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={!note.trim() || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Save className="mr-2 h-4 w-4 animate-pulse" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Note
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
